import {
  useMutation,
  type UseMutationResult,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createUploadToken,
  listUploadTokens,
  revokeUploadToken,
  testUploadToken,
  updateUploadToken,
  type UploadTokenCreateInput,
  type UploadTokenCreateResponse,
  type UploadTokenTestInput,
  type UploadTokenTestResult,
  type UploadTokenUpdateInput,
} from '@/features/uploads/data/uploadTokens';
import { invalidateKey, queryKeys, useAccessToken, useInvalidateQueries } from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type { UploadTokenDto } from '@flowqa/shared';

export const useUploadTokensQuery = (
  projectId: string | null,
): UseQueryResult<UploadTokenDto[], ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<UploadTokenDto[], ApiError>({
    queryKey: queryKeys.uploadTokens(projectId, orgKey),
    enabled: Boolean(token && projectId),
    queryFn: async (): Promise<UploadTokenDto[]> => {
      if (!projectId) return [];
      return await listUploadTokens(projectId, orgForQuery);
    },
    staleTime: 15_000,
  });
};

export const useCreateUploadToken = (): UseMutationResult<
  UploadTokenCreateResponse,
  ApiError,
  { projectId: string; input: UploadTokenCreateInput }
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useMutation<
    UploadTokenCreateResponse,
    ApiError,
    { projectId: string; input: UploadTokenCreateInput }
  >({
    mutationFn: async ({ projectId, input }): Promise<UploadTokenCreateResponse> => {
      return await createUploadToken(projectId, input, orgForQuery);
    },
    onSuccess: async (_result, vars) => {
      await invalidateQueries([invalidateKey(queryKeys.uploadTokens(vars.projectId, orgKey))]);
    },
  });
};

export const useRevokeUploadToken = (): UseMutationResult<
  UploadTokenDto,
  ApiError,
  { projectId: string; tokenId: string }
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useMutation<UploadTokenDto, ApiError, { projectId: string; tokenId: string }>({
    mutationFn: async ({ projectId, tokenId }): Promise<UploadTokenDto> => {
      return await revokeUploadToken(projectId, tokenId, orgForQuery);
    },
    onSuccess: async (_result, vars) => {
      await invalidateQueries([invalidateKey(queryKeys.uploadTokens(vars.projectId, orgKey))]);
    },
  });
};

export const useUpdateUploadToken = (): UseMutationResult<
  UploadTokenDto,
  ApiError,
  { projectId: string; tokenId: string; input: UploadTokenUpdateInput }
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useMutation<
    UploadTokenDto,
    ApiError,
    { projectId: string; tokenId: string; input: UploadTokenUpdateInput }
  >({
    mutationFn: async ({ projectId, tokenId, input }): Promise<UploadTokenDto> => {
      return await updateUploadToken(projectId, tokenId, input, orgForQuery);
    },
    onSuccess: async (_result, vars) => {
      await invalidateQueries([invalidateKey(queryKeys.uploadTokens(vars.projectId, orgKey))]);
    },
  });
};

export const useTestUploadToken = (): UseMutationResult<
  UploadTokenTestResult,
  ApiError,
  { projectId: string; input: UploadTokenTestInput }
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useMutation<
    UploadTokenTestResult,
    ApiError,
    { projectId: string; input: UploadTokenTestInput }
  >({
    mutationFn: async ({ projectId, input }): Promise<UploadTokenTestResult> => {
      return await testUploadToken(projectId, input, orgForQuery);
    },
    onSuccess: async (_result, vars) => {
      await invalidateQueries([invalidateKey(queryKeys.uploadTokens(vars.projectId, orgKey))]);
    },
  });
};
